import { useEffect, useState } from 'react';
import { Upload } from 'lucide-react';
import type { EventVendorRecipient } from '@peles/shared';
import { ComposeEmailModal } from '../messaging/ComposeEmailModal';
import { useSendVendorEmail } from '../../hooks/useEvents';

export interface ComposeVendorEmailModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Selected vendors — all must already have a resolved email. */
  recipients: EventVendorRecipient[];
}

/**
 * Compose an email to the selected event vendors. A thin wrapper over the
 * generic {@link ComposeEmailModal} that adds vendor-specific behaviour: the
 * recipients come from the vendor selection, and an optional per-vendor
 * "upload a file" link can be appended to each email.
 */
export function ComposeVendorEmailModal({
  open,
  onClose,
  eventId,
  recipients,
}: ComposeVendorEmailModalProps) {
  const [includeUploadLink, setIncludeUploadLink] = useState(false);
  const sendEmail = useSendVendorEmail(eventId);

  useEffect(() => {
    if (open) setIncludeUploadLink(false);
  }, [open]);

  return (
    <ComposeEmailModal
      open={open}
      onClose={onClose}
      title="Email Vendors"
      fixedRecipients={recipients.map((r) => ({
        id: r.vendorId,
        label: r.businessName,
        email: r.email,
      }))}
      allowManualRecipients
      eventIdForDocuments={eventId}
      extraOptions={
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeUploadLink}
            onChange={(e) => setIncludeUploadLink(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            data-testid="include-upload-link"
          />
          <span>
            <span className="flex items-center gap-1 font-medium">
              <Upload size={13} />
              Include a file-upload link
            </span>
            <span className="text-xs text-gray-500">
              Each vendor gets a secure button to upload a file, attached to them on this event.
              You'll be notified when they do.
            </span>
          </span>
        </label>
      }
      sendLabel={(count) => `Send to ${count} recipient${count === 1 ? '' : 's'}`}
      onSend={async (payload) => {
        const result = await sendEmail.mutateAsync({
          vendorIds: recipients.map((r) => r.vendorId),
          subject: payload.subject,
          bodyHtml: payload.bodyHtml,
          attachments: payload.attachments,
          ccSelf: payload.ccSelf,
          additionalEmails: payload.additionalEmails,
          includeUploadLink,
        });
        return {
          sentCount: result.sent.length,
          skipped: result.skipped.map((skip) => ({
            label:
              recipients.find((r) => r.vendorId === skip.vendorId)?.businessName ?? skip.vendorId,
            reason: skip.reason,
          })),
        };
      }}
    />
  );
}
